import { useEffect, useState } from "react";
import { MessageCircle, BadgeCheck, Timer } from "lucide-react";
import { Reveal, waLink } from "./shared";

const useCountdown = () => {
  const [left, setLeft] = useState({ d: 0, h: 0, m: 0, s: 0 });
  useEffect(() => {
    const target = new Date();
    target.setMonth(target.getMonth() + 1, 1);
    target.setHours(0, 0, 0, 0);
    const tick = () => {
      const diff = Math.max(0, target - new Date());
      setLeft({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff / 3600000) % 24),
        m: Math.floor((diff / 60000) % 60),
        s: Math.floor((diff / 1000) % 60),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return left;
};

const pad = (n) => String(n).padStart(2, "0");

export const SpecialOffer = () => {
  const { d, h, m, s } = useCountdown();
  const units = [
    { v: pad(d), l: "Days" },
    { v: pad(h), l: "Hours" },
    { v: pad(m), l: "Mins" },
    { v: pad(s), l: "Secs" },
  ];

  return (
    <section id="offer" data-testid="special-offer-section" className="relative py-24 sm:py-32 bg-void overflow-hidden">
      <div className="absolute top-0 left-1/4 w-[30rem] h-[30rem] rounded-full bg-brand-gold/10 blur-[140px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-brand-gold mb-4">
              Limited Time Only
            </p>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black uppercase leading-[0.92] tracking-tight text-white">
              Special <span className="text-brand-lime text-glow-lime">Offer!</span>
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <div className="mt-6 inline-flex items-end gap-3 border border-brand-lime/50 bg-brand-lime/10 rounded-2xl px-6 py-4 clip-corner">
              <span className="font-display text-xl font-bold uppercase text-white leading-none">Up<br />To</span>
              <span className="font-display text-7xl sm:text-8xl font-black text-brand-lime text-glow-lime leading-none">20%</span>
              <span className="font-display text-2xl font-bold uppercase text-white leading-none pb-1">Off</span>
            </div>
          </Reveal>
          <Reveal delay={0.24}>
            <p className="mt-6 text-slate-400 text-base max-w-lg leading-relaxed">
              Premium service · Affordable price. Complimentary hardware
              inspection plus 20% off labour on SSD & RAM upgrade bundles this
              month. Satisfaction guaranteed.
            </p>
          </Reveal>
          <Reveal delay={0.32}>
            <div className="mt-8 flex items-center gap-3" data-testid="offer-countdown">
              <Timer className="w-5 h-5 text-brand-gold" />
              {units.map((u) => (
                <div key={u.l} className="text-center bg-navy border border-white/10 rounded-lg px-3 py-2 min-w-[62px]">
                  <p className="font-display text-2xl font-extrabold text-white leading-none">{u.v}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">{u.l}</p>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.4}>
            <a
              data-testid="offer-claim-button"
              href={waLink("Hi AFZ Tech Solution, I want to claim the 20% OFF special offer on my repair/upgrade.")}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-9 inline-flex items-center gap-3 bg-brand-lime text-void font-display font-bold uppercase tracking-wide text-lg px-8 py-4 rounded-full hover:bg-brand-limedark hover:scale-[1.03] transition-all duration-300 shadow-[0_0_30px_rgba(170,255,0,0.4)]"
            >
              <MessageCircle className="w-5 h-5" />
              Claim on WhatsApp
            </a>
          </Reveal>
        </div>

        <Reveal delay={0.2} className="relative">
          <div className="relative clip-corner border border-brand-lime/30 overflow-hidden shadow-[0_0_50px_rgba(170,255,0,0.15)]">
            <img
              src="/assets/collateral-3.jpg"
              alt="AFZ special offer — up to 20% off computer service"
              className="w-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-void/70 via-transparent to-transparent" />
          </div>
          <div className="absolute bottom-4 left-4 sm:-bottom-5 sm:-left-5 bg-navy border border-brand-gold/50 rounded-xl px-4 py-2.5 sm:px-5 sm:py-3 flex items-center gap-3 shadow-[0_0_24px_rgba(245,158,11,0.25)]">
            <BadgeCheck className="w-6 h-6 text-brand-gold" />
            <div>
              <p className="font-display font-bold uppercase text-white text-sm leading-none">Satisfaction Guaranteed</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">Quality Service</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
